import type { NextPage } from "next";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";

import LoadingPage from "../components/LoadingPage";
import { WithSidebar } from "../components/Sidebar";
import { Tab, TabGroup } from "../components/Tab";
import { EnvironmentManagement } from "../components/EnvironmentManagement";
import RecoveryManagementView from "../components/RecoveryManagementView";
import TokenManagementView from "../components/TokenManagementView";
import UserManagementView from "../components/UserManagementView";

import useUser from "../src/useUser";
import useTenant from "../src/useTenant";
import { tenantDisplayName } from "../src/useTenant";
import FallbackManagementView from "@/components/FallbackManagementView";
import MainContent from "@/components/MainContent";

const Settings: NextPage = () => {
  const { isLoading } = useUser();
  const { tenant } = useTenant();
  const { query } = useRouter();

  if (isLoading || !tenant) {
    return <LoadingPage />;
  }

  const tabs = [
    {
      tabKey: "api-keys",
      title: "API Keys",
      contents: <TokenManagementView />,
    },
    {
      tabKey: "users",
      title: "Users",
      contents: <UserManagementView />,
    },
    {
      tabKey: "environments",
      title: "Environments",
      contents: <EnvironmentManagement />,
    },
    {
      tabKey: "recovery",
      title: "Recovery",
      contents: <RecoveryManagementView />,
    },
    {
      tabKey: "fallback",
      title: "Fallback",
      contents: <FallbackManagementView />,
    },
  ];

  const tabKeys = tabs.map((t) => t.tabKey);

  const selectedTabKey =
    query.tab && tabKeys.includes(query.tab.toString())
      ? query.tab.toString()
      : tabKeys[0];

  const selectedTab = tabs.find((t) => t.tabKey === selectedTabKey);

  return (
    <>
      <Head>
        <title>Oso Cloud - Organization Settings</title>
      </Head>
      <WithSidebar>
        <MainContent>
          <div className="font-space text-3xl">Organization Settings</div>
          <div className="text-xl">{tenantDisplayName(tenant)}</div>
          <div className="text-xs text-gray-500">{`ID: ${tenant.id}`}</div>

          <TabGroup>
            {tabs.map(({ tabKey, title }) => (
              <Link href={`?tab=${tabKey}`} passHref key={tabKey}>
                <Tab title={title} isSelected={selectedTabKey === tabKey} />
              </Link>
            ))}
          </TabGroup>
          <div className="pt-8 max-w-4xl">
            {selectedTab ? selectedTab.contents : <div />}
          </div>
        </MainContent>
      </WithSidebar>
    </>
  );
};

export default Settings;
